// The firm's form catalogue: the edition each file in use prints, its
// chronology and the ready packages built on something else. Every read is
// staff; the checks, the re-download and a decision are admin writes.

import type {
  AffectedPackagesResponse,
  FormCatalogueRow,
  FormEditionChanges,
  FormEditionDecisionInput,
  FormEditionRow,
} from "@duralex/shared";
import { downloadStaffFile, fetchStaffBlob, getAuthHeaders, refusal } from "./auth";

export interface FormCheckOutcome {
  formId: string;
  status: "current" | "updated" | "failed";
  oldVersion?: string;
  newVersion?: string;
  error?: string;
}

export interface FormCheckRun {
  checked: number;
  updated: number;
  failed: number;
  results: FormCheckOutcome[];
}

async function readJson<T>(res: Response, what: string): Promise<T> {
  if (res.ok) return (await res.json()) as T;
  throw await refusal(res, `${what} failed: ${res.status}`);
}

function getJson<T>(url: string, what: string): Promise<T> {
  return fetch(url, { headers: { ...getAuthHeaders() }, credentials: "same-origin" }).then((res) =>
    readJson<T>(res, what),
  );
}

function sendJson<T>(
  method: "POST" | "PUT",
  url: string,
  what: string,
  body?: unknown,
): Promise<T> {
  return fetch(url, {
    method,
    headers: {
      ...(body === undefined ? {} : { "content-type": "application/json" }),
      ...getAuthHeaders(),
    },
    credentials: "same-origin",
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then((res) => readJson<T>(res, what));
}

export function fetchForms(): Promise<FormCatalogueRow[]> {
  return getJson("/api/forms", "Reading the form catalogue");
}

export function fetchFormEditions(formId: string): Promise<FormEditionRow[]> {
  return getJson(`/api/forms/${encodeURIComponent(formId)}/editions`, "Reading the chronology");
}

export function fetchEditionChanges(
  formId: string,
  editionKey: string,
): Promise<FormEditionChanges> {
  return getJson(
    `/api/forms/${encodeURIComponent(formId)}/editions/${encodeURIComponent(editionKey)}/changes`,
    "Reading what changed",
  );
}

export function fetchAffectedPackages(formId: string): Promise<AffectedPackagesResponse> {
  return getJson(
    `/api/filing-packages/affected/${encodeURIComponent(formId)}`,
    "Reading the affected packages",
  );
}

export function decideEdition(
  formId: string,
  editionKey: string,
  body: FormEditionDecisionInput,
): Promise<FormEditionRow> {
  return sendJson(
    "PUT",
    `/api/forms/${encodeURIComponent(formId)}/editions/${encodeURIComponent(editionKey)}/decision`,
    "Recording the decision",
    body,
  );
}

/** Waits: the server answers once the file at the URL has been compared. */
export function checkForm(formId: string): Promise<FormCheckOutcome> {
  return sendJson("POST", `/api/forms/${encodeURIComponent(formId)}/check-version`, "The check");
}

export function checkAllForms(): Promise<FormCheckRun> {
  return sendJson("POST", "/api/forms/check-versions", "The check");
}

/** Answers at once; the files arrive in the background. */
export function redownloadForms(): Promise<{ started: boolean }> {
  return sendJson("POST", "/api/forms/refresh", "The download");
}

export function fetchFormBlob(formId: string): Promise<Blob> {
  return fetchStaffBlob(`/api/forms/${encodeURIComponent(formId)}/download`, "form");
}

export function downloadFormPdf(formId: string): Promise<void> {
  return downloadStaffFile(
    `/api/forms/${encodeURIComponent(formId)}/download`,
    `${formId}.pdf`,
    "form",
  );
}
